import dayjs, { type Dayjs } from "dayjs";

// Single source of truth for deadline countdowns across mobil and center.
// Consolidates the three prior forks: workOrder/detail-view/tone.ts
// (day-granular, brand tones), mobil collabs/cards helpers.timeChipFor
// (hour-granular chips) and center activeCreators/autoTransition.daysUntil.

export type Urgency = "vencido" | "hoy" | "manana" | "esta_semana" | "calmo";

// Tailwind class names resolving through the brand palette. Consumers
// compose them with their own layout classes.
export type CountdownTone = {
  bgClass: string;
  textClass: string;
  iconClass: string;
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

type DateInput = Dayjs | Date | string | null | undefined;

export function toDayjs(value: DateInput): Dayjs | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

export function urgencyOf(value: DateInput): Urgency {
  const target = toDayjs(value);
  if (!target) return "calmo";
  const diff = target.startOf("day").diff(dayjs().startOf("day"), "day");
  if (diff < 0) return "vencido";
  if (diff === 0) return "hoy";
  if (diff === 1) return "manana";
  if (diff <= 6) return "esta_semana";
  return "calmo";
}

export function urgencyTone(u: Urgency): CountdownTone {
  if (u === "vencido" || u === "hoy") {
    return {
      bgClass: "bg-brand-400",
      textClass: "text-typography-white",
      iconClass: "text-typography-white",
    };
  }
  if (u === "manana" || u === "esta_semana") {
    return {
      bgClass: "bg-brand-100",
      textClass: "text-typography-950",
      iconClass: "text-brand-400",
    };
  }
  return {
    bgClass: "bg-brand-50",
    textClass: "text-brand-400",
    iconClass: "text-brand-400",
  };
}

// Day-granular sentence label: "Vencido hace 3 días" / "Hoy" / "Mañana" /
// "En N días" / "Sin fecha".
export function countdownLabel(value: DateInput): string {
  const target = toDayjs(value);
  if (!target) return "Sin fecha";
  const diff = target.startOf("day").diff(dayjs().startOf("day"), "day");
  if (diff < 0) {
    const overdue = Math.abs(diff);
    return `Vencido hace ${overdue} ${overdue === 1 ? "día" : "días"}`;
  }
  if (diff === 0) return "Hoy";
  if (diff === 1) return "Mañana";
  return `En ${diff} días`;
}

// Compact chip label, hour-granular inside 24h: "Vencido" / "HOY" / "5h" /
// "2d". `urgent` flags the <=48h window (and overdue).
export function timeChip(
  value: DateInput,
): { label: string; urgent: boolean } | null {
  const target = toDayjs(value);
  if (!target) return null;
  const hrs = Math.round(target.diff(dayjs(), "hour", true));
  if (hrs < 0) return { label: "Vencido", urgent: true };
  if (hrs === 0) return { label: "HOY", urgent: true };
  if (hrs <= 24) return { label: `${hrs}h`, urgent: true };
  const days = Math.round(hrs / 24);
  if (hrs <= 48) return { label: `${days}d`, urgent: true };
  return { label: `${days}d`, urgent: false };
}

// Time left until a deadline at its two largest units: "2 d 23 h" / "3 d" / "5 h" /
// "40 min". Minutes only show below one hour, and never read "0 min" while time
// remains. `now` is the caller's clock, so a screen on a live ticker stays current.
export function timeLeftLabel(
  value: DateInput,
  now: Date = new Date(),
): string | null {
  const target = toDayjs(value);
  if (!target) return null;
  const msLeft = Math.max(0, target.diff(now));
  const days = Math.floor(msLeft / DAY_MS);
  const hours = Math.floor((msLeft % DAY_MS) / HOUR_MS);
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return `${hours} h`;
  return `${Math.ceil(msLeft / MINUTE_MS)} min`;
}

// Floors at 0 — an overdue deadline (e.g. race with a background job)
// never displays as negative.
export function daysUntil(value: DateInput): number {
  const target = toDayjs(value);
  if (!target) return 0;
  const diff = target.startOf("day").diff(dayjs().startOf("day"), "day");
  return Math.max(diff, 0);
}

// Signed day difference: negative when overdue. For "atrasada" logic.
export function daysSinceDeadline(value: DateInput): number | null {
  const target = toDayjs(value);
  if (!target) return null;
  return dayjs().startOf("day").diff(target.startOf("day"), "day");
}

export function dayLabel(n: number): string {
  return n === 1 ? "día" : "días";
}

export function fullDate(value: DateInput): string | null {
  const target = toDayjs(value);
  if (!target) return null;
  return target.format("DD [de] MMMM");
}

// Day and month without a padded day: "9 de octubre".
export function shortDate(value: DateInput): string | null {
  const target = toDayjs(value);
  if (!target) return null;
  return target.format("D [de] MMMM");
}
